import React, { useEffect, useState } from 'react';
import {
    Alert,
    Box,
    Button,
    CardContent,
    Chip,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    IconButton,
    MenuItem,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    TextField
} from '@mui/material';
import { IconCalendarTime, IconPlus, IconCheck, IconX } from '@tabler/icons';
import api from '../../../../services/api';
import PageHeader from '../../../../../ui-component/extended/PageHeader';
import GlassCard from '../../../../../ui-component/extended/GlassCard';

const LEAVE_TYPE_LABEL = {
    vacation: 'Férias',
    sick_leave: 'Licença Médica',
    maternity_paternity: 'Licença Maternidade/Paternidade',
    unpaid: 'Licença Não Remunerada',
    other: 'Outro'
};

const STATUS_LABEL = {
    requested: { label: 'Solicitado', color: 'warning' },
    approved: { label: 'Aprovado', color: 'info' },
    in_progress: { label: 'Em Andamento', color: 'primary' },
    finished: { label: 'Concluído', color: 'success' },
    cancelled: { label: 'Cancelado', color: 'default' }
};

const emptyForm = { colaborador: '', leave_type: 'vacation', start_date: '', end_date: '', notes: '' };

const LeaveRequests = () => {
    const [appId, setAppId] = useState(null);
    const [leaveRequests, setLeaveRequests] = useState([]);
    const [colaboradores, setColaboradores] = useState([]);
    const [colaboradorFilter, setColaboradorFilter] = useState('');
    const [statusFilter, setStatusFilter] = useState('');
    const [status, setStatus] = useState({ type: '', message: '' });
    const [dialogOpen, setDialogOpen] = useState(false);
    const [form, setForm] = useState(emptyForm);
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        (async () => {
            try {
                const res = await api.get('v1/apps/default/');
                setAppId(res.data.app.id);
            } catch (error) {
                console.error(error);
            }
        })();
    }, []);

    useEffect(() => {
        if (!appId) return;
        loadColaboradores();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [appId]);

    useEffect(() => {
        if (!appId) return;
        loadLeaveRequests();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [appId, colaboradorFilter, statusFilter]);

    const loadColaboradores = async () => {
        try {
            const res = await api.get(`v1/apps/${appId}/colaboradores/`);
            setColaboradores(res.data.colaboradores || []);
        } catch (error) {
            console.error(error);
        }
    };

    const loadLeaveRequests = async () => {
        try {
            const params = {};
            if (colaboradorFilter) params.colaborador = colaboradorFilter;
            if (statusFilter) params.status = statusFilter;
            const res = await api.get(`v1/apps/${appId}/leave_requests/`, { params });
            setLeaveRequests(res.data.leave_requests || []);
        } catch (error) {
            console.error(error);
            setStatus({ type: 'error', message: 'Erro ao carregar solicitações' });
        }
    };

    const handleOpenCreate = () => {
        setForm(emptyForm);
        setDialogOpen(true);
    };

    const handleSave = async () => {
        if (!form.colaborador) {
            setStatus({ type: 'error', message: 'Selecione o colaborador' });
            return;
        }
        if (!form.start_date || !form.end_date) {
            setStatus({ type: 'error', message: 'Informe as datas de início e fim' });
            return;
        }
        setSaving(true);
        try {
            await api.post(`v1/apps/${appId}/leave_requests/`, form);
            setDialogOpen(false);
            loadLeaveRequests();
        } catch (error) {
            console.error(error);
            setStatus({ type: 'error', message: error.response?.data?.error || 'Erro ao criar solicitação' });
        } finally {
            setSaving(false);
        }
    };

    const handleApprove = async (leaveRequest) => {
        try {
            await api.post(`v1/apps/${appId}/leave_requests/${leaveRequest.id}/approve/`);
            loadLeaveRequests();
        } catch (error) {
            console.error(error);
            setStatus({ type: 'error', message: error.response?.data?.error || 'Erro ao aprovar solicitação' });
        }
    };

    const handleCancel = async (leaveRequest) => {
        if (!window.confirm('Cancelar esta solicitação?')) return;
        try {
            await api.post(`v1/apps/${appId}/leave_requests/${leaveRequest.id}/cancel/`);
            loadLeaveRequests();
        } catch (error) {
            console.error(error);
            setStatus({ type: 'error', message: error.response?.data?.error || 'Erro ao cancelar solicitação' });
        }
    };

    return (
        <Box>
            <PageHeader
                icon={IconCalendarTime}
                gradient="blue"
                title="Férias e Afastamentos"
                subtitle="Controle de períodos de férias, licenças e afastamentos"
                actions={
                    <Button variant="contained" startIcon={<IconPlus size={18} />} onClick={handleOpenCreate}>
                        Nova Solicitação
                    </Button>
                }
            />

            {status.message && (
                <Alert severity={status.type} sx={{ mb: 2 }} onClose={() => setStatus({ type: '', message: '' })}>
                    {status.message}
                </Alert>
            )}

            <Stack direction="row" spacing={2} sx={{ mb: 2 }}>
                <TextField
                    select
                    label="Colaborador"
                    size="small"
                    sx={{ minWidth: 220 }}
                    value={colaboradorFilter}
                    onChange={(e) => setColaboradorFilter(e.target.value)}
                >
                    <MenuItem value="">Todos</MenuItem>
                    {colaboradores.map((colaborador) => (
                        <MenuItem key={colaborador.id} value={colaborador.id}>
                            {colaborador.full_name}
                        </MenuItem>
                    ))}
                </TextField>
                <TextField
                    select
                    label="Status"
                    size="small"
                    sx={{ minWidth: 180 }}
                    value={statusFilter}
                    onChange={(e) => setStatusFilter(e.target.value)}
                >
                    <MenuItem value="">Todos</MenuItem>
                    {Object.entries(STATUS_LABEL).map(([value, info]) => (
                        <MenuItem key={value} value={value}>
                            {info.label}
                        </MenuItem>
                    ))}
                </TextField>
            </Stack>

            <GlassCard>
                <CardContent>
                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell>Colaborador</TableCell>
                                <TableCell>Tipo</TableCell>
                                <TableCell>Início</TableCell>
                                <TableCell>Fim</TableCell>
                                <TableCell>Status</TableCell>
                                <TableCell align="right" />
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {leaveRequests.map((leaveRequest) => {
                                const statusInfo = STATUS_LABEL[leaveRequest.status] || { label: leaveRequest.status, color: 'default' };
                                return (
                                    <TableRow key={leaveRequest.id} hover>
                                        <TableCell>{leaveRequest.colaborador_name}</TableCell>
                                        <TableCell>{LEAVE_TYPE_LABEL[leaveRequest.leave_type] || leaveRequest.leave_type}</TableCell>
                                        <TableCell>{leaveRequest.start_date}</TableCell>
                                        <TableCell>{leaveRequest.end_date}</TableCell>
                                        <TableCell>
                                            <Chip label={statusInfo.label} color={statusInfo.color} size="small" />
                                        </TableCell>
                                        <TableCell align="right">
                                            {leaveRequest.status === 'requested' && (
                                                <IconButton size="small" onClick={() => handleApprove(leaveRequest)}>
                                                    <IconCheck size={16} />
                                                </IconButton>
                                            )}
                                            {['requested', 'approved', 'in_progress'].includes(leaveRequest.status) && (
                                                <IconButton size="small" onClick={() => handleCancel(leaveRequest)}>
                                                    <IconX size={16} />
                                                </IconButton>
                                            )}
                                        </TableCell>
                                    </TableRow>
                                );
                            })}
                            {leaveRequests.length === 0 && (
                                <TableRow>
                                    <TableCell colSpan={6} align="center">
                                        Nenhuma solicitação cadastrada ainda
                                    </TableCell>
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>
                </CardContent>
            </GlassCard>

            <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
                <DialogTitle>Nova Solicitação</DialogTitle>
                <DialogContent>
                    <Stack spacing={2} sx={{ mt: 1 }}>
                        <TextField
                            select
                            label="Colaborador"
                            fullWidth
                            required
                            value={form.colaborador}
                            onChange={(e) => setForm({ ...form, colaborador: e.target.value })}
                        >
                            {colaboradores.map((colaborador) => (
                                <MenuItem key={colaborador.id} value={colaborador.id}>
                                    {colaborador.full_name}
                                </MenuItem>
                            ))}
                        </TextField>
                        <TextField
                            select
                            label="Tipo"
                            fullWidth
                            value={form.leave_type}
                            onChange={(e) => setForm({ ...form, leave_type: e.target.value })}
                        >
                            {Object.entries(LEAVE_TYPE_LABEL).map(([value, label]) => (
                                <MenuItem key={value} value={value}>
                                    {label}
                                </MenuItem>
                            ))}
                        </TextField>
                        <TextField
                            label="Data de início"
                            type="date"
                            fullWidth
                            required
                            InputLabelProps={{ shrink: true }}
                            value={form.start_date}
                            onChange={(e) => setForm({ ...form, start_date: e.target.value })}
                        />
                        <TextField
                            label="Data de fim"
                            type="date"
                            fullWidth
                            required
                            InputLabelProps={{ shrink: true }}
                            value={form.end_date}
                            onChange={(e) => setForm({ ...form, end_date: e.target.value })}
                        />
                        <TextField
                            label="Observações (opcional)"
                            fullWidth
                            multiline
                            minRows={2}
                            value={form.notes}
                            onChange={(e) => setForm({ ...form, notes: e.target.value })}
                        />
                    </Stack>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDialogOpen(false)}>Cancelar</Button>
                    <Button variant="contained" onClick={handleSave} disabled={saving}>
                        {saving ? 'Salvando...' : 'Salvar'}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default LeaveRequests;
